/**
 * FocusModeWrapperV2 - Focus mode layout wrapper
 *
 * Features:
 * - Fullscreen API integration
 * - Minimalist UI when active
 * - Exit button and keyboard shortcut
 * - Smooth transitions
 */

import { memo, useEffect, useCallback } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectFormattedStageTime
} from '../../../hooks/useChallenge'

// Icons
const MinimizeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
    />
  </svg>
)

const FocusModeWrapperV2 = memo(({ children, isActive }) => {
  const stageTime = useChallengeStoreV2(selectFormattedStageTime)
  const exitFocusMode = useChallengeStoreV2((state) => state.exitFocusMode)

  // Listen for fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      // If user exits fullscreen via Escape or browser UI, update state
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        exitFocusMode()
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [exitFocusMode])

  // Handle exit
  const handleExit = useCallback(() => {
    exitFocusMode()
  }, [exitFocusMode])

  if (!isActive) {
    return <>{children}</>
  }

  return (
    <div className="force-light-mode fixed inset-0 z-50 bg-gray-50 overflow-hidden flex flex-col">
      {/* Minimal header */}
      <header className="flex-shrink-0 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between">
          {/* Timer */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-sm font-mono font-medium text-gray-700">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="tabular-nums">{stageTime}</span>
          </div>

          {/* Focus mode indicator and exit */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-sm text-indigo-600">
              <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
              Focus Mode
            </span>

            <button
              onClick={handleExit}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
                text-gray-600 hover:bg-gray-100
                transition-colors"
              title="Exit focus mode (Escape)"
            >
              <MinimizeIcon />
              <span className="hidden sm:inline">Exit</span>
              <kbd className="hidden md:inline ml-1 px-1.5 py-0.5 text-xs bg-gray-100 rounded border border-gray-300">
                Esc
              </kbd>
            </button>
          </div>
        </div>
      </header>

      {/* Content area */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6">{children}</div>
      </main>
    </div>
  )
})

FocusModeWrapperV2.displayName = 'FocusModeWrapperV2'

export default FocusModeWrapperV2
